import { Controller } from '@hotwired/stimulus'

let accordionInstanceCounter = 0
const HIDE_FALLBACK_MS = 400

/**
 * @typedef {{ immediate?: boolean }} FAQStateOptions
 */

/**
 * @typedef {(event?: TransitionEvent) => void} FAQHideHandler
 */

/**
 * @typedef {HTMLElement & {
 *   _faqHideHandler?: FAQHideHandler | null,
 *   _faqHideTimer?: number | null
 * }} FAQContentElement
 */

/**
 * Stimulus-контроллер FAQ-аккордеона.
 *
 * Отвечает за открытие и закрытие пунктов FAQ, поддержку режима с несколькими
 * открытыми пунктами, синхронизацию ARIA-атрибутов и скрытие контента после
 * завершения CSS-анимации.
 *
 * @extends Controller
 */
export default class FAQController extends Controller {
  /**
   * Целевые элементы контроллера: пункт аккордеона, его контент и кнопка.
   * @type {string[]}
   */
  static targets = ['item', 'content', 'button']

  /**
   * Настройки контроллера из data-атрибутов.
   *
   * `allowMultiple` разрешает держать открытыми несколько пунктов FAQ
   * одновременно.
   * @type {{ allowMultiple: { type: BooleanConstructor, default: boolean } }}
   */
  static values = {
    allowMultiple: { type: Boolean, default: false }
  }

  /**
   * Инициализирует состояние аккордеона после подключения контроллера.
   *
   * Открывает пункты с `data-default-open` или классом `is-open`. Если режим
   * нескольких открытых пунктов выключен, оставляет открытым только первый
   * подходящий пункт.
   * @returns {void}
   */
  connect() {
    this.accordionId =
      this.accordionId || `accordion-${++accordionInstanceCounter}`
    this.element.classList.add('is-enhanced')

    if (this.allowMultipleValue) {
      this.itemTargets.forEach((item) => {
        if (
          item.hasAttribute('data-default-open') ||
          item.classList.contains('is-open')
        ) {
          this.open(item, { immediate: true })
        } else {
          this.close(item, { immediate: true })
        }
      })
    } else {
      const itemToKeepOpen =
        this.itemTargets.find((item) =>
          item.hasAttribute('data-default-open')
        ) || this.itemTargets.find((item) => item.classList.contains('is-open'))

      this.itemTargets.forEach((item) => {
        if (item === itemToKeepOpen) {
          this.open(item, { immediate: true })
        } else {
          this.close(item, { immediate: true })
        }
      })
    }

    this.openFromHash()
  }

  /**
   * Раскрывает пункт, на который указывает якорь в адресе.
   *
   * Нужен для ссылки «Все города» со страницы страны: она ведёт на общий
   * каталог с якорем на карточку страны, а по умолчанию сервер открывает
   * только первую страну в списке — без этого посетитель доскроллил бы до
   * нужной страны, но увидел свёрнутый список городов.
   *
   * Скролл к элементу браузер делает сам, здесь только состояние аккордеона.
   * @returns {void}
   */
  openFromHash() {
    const hash = window.location.hash.slice(1)
    if (!hash) return

    const target = this.element.querySelector(`#${CSS.escape(hash)}`)
    const item = target?.closest('[data-faq-target="item"]')
    if (!item || !this.itemTargets.includes(item)) return

    if (!this.allowMultipleValue) {
      this.itemTargets.forEach((other) => {
        if (other !== item) this.close(other, { immediate: true })
      })
    }

    this.open(item, { immediate: true })
  }

  /**
   * Переключает состояние пункта FAQ, из кнопки которого пришло событие.
   *
   * В одиночном режиме перед открытием текущего пункта закрывает остальные
   * открытые пункты.
   * @param {MouseEvent} event - Событие клика по кнопке пункта FAQ.
   * @returns {void}
   */
  toggle(event) {
    const currentItem = event?.currentTarget.closest('[data-faq-target="item"]')
    if (!currentItem) return

    event?.preventDefault()

    const isOpen = currentItem.classList.contains('is-open')

    if (!this.allowMultipleValue) {
      this.itemTargets.forEach((item) => {
        if (item !== currentItem && item.classList.contains('is-open')) {
          this.close(item)
        }
      })
    }

    if (isOpen) {
      this.close(currentItem)
    } else {
      this.open(currentItem)
    }
  }

  /**
   * Открывает пункт FAQ и синхронизирует доступное состояние кнопки и контента.
   * @param {HTMLElement} item - Корневой элемент пункта FAQ.
   * @param {FAQStateOptions} [options] - Параметры изменения состояния.
   * @returns {void}
   */
  open(item, { immediate = false } = {}) {
    const trigger = this.triggerFor(item)
    const content = this.contentFor(item)

    if (!trigger || !content) return

    this.cancelPendingHide(content)
    content.hidden = false
    content.setAttribute('aria-hidden', 'false')
    trigger.setAttribute('aria-expanded', 'true')

    if (!immediate) {
      // Форсируем применение hidden=false до переключения класса, чтобы transition проигрался.
      void content.offsetHeight
    }

    item.classList.add('is-open')
  }

  /**
   * Закрывает пункт FAQ и планирует скрытие контента после анимации.
   * @param {HTMLElement} item - Корневой элемент пункта FAQ.
   * @param {FAQStateOptions} [options] - Параметры изменения состояния.
   * @returns {void}
   */
  close(item, { immediate = false } = {}) {
    const trigger = this.triggerFor(item)
    const content = this.contentFor(item)

    if (!trigger || !content) return

    this.cancelPendingHide(content)
    item.classList.remove('is-open')
    trigger.setAttribute('aria-expanded', 'false')
    content.setAttribute('aria-hidden', 'true')

    if (immediate) {
      content.hidden = true
      return
    }

    this.scheduleHide(item, content)
  }

  /**
   * Возвращает кнопку, управляющую указанным пунктом FAQ.
   * @param {HTMLElement} item - Корневой элемент пункта FAQ.
   * @returns {HTMLElement | null} Кнопка пункта или `null`, если она не найдена.
   */
  triggerFor(item) {
    return item.querySelector('[data-faq-target="button"]')
  }

  /**
   * Возвращает контент указанного пункта FAQ.
   * @param {HTMLElement} item - Корневой элемент пункта FAQ.
   * @returns {FAQContentElement | null} Контент пункта или `null`, если он не найден.
   */
  contentFor(item) {
    return item.querySelector('[data-faq-target="content"]')
  }

  /**
   * Скрывает контент закрытого пункта после окончания transition.
   *
   * Таймер используется как резервный путь на случай, если событие
   * `transitionend` не придёт.
   * @param {HTMLElement} item - Корневой элемент пункта FAQ.
   * @param {FAQContentElement} content - Контент закрываемого пункта.
   * @returns {void}
   */
  scheduleHide(item, content) {
    const hideContent = (event) => {
      if (event && event.target !== content) return
      if (item.classList.contains('is-open')) return

      content.hidden = true
      this.cancelPendingHide(content)
    }

    const hideTimer = window.setTimeout(() => {
      hideContent()
    }, HIDE_FALLBACK_MS)

    content.addEventListener('transitionend', hideContent)
    content._faqHideHandler = hideContent
    content._faqHideTimer = hideTimer
  }

  /**
   * Отменяет отложенное скрытие контента, если пункт снова открыли до окончания
   * анимации закрытия.
   * @param {FAQContentElement} content - Контент пункта FAQ.
   * @returns {void}
   */
  cancelPendingHide(content) {
    if (content._faqHideHandler) {
      content.removeEventListener('transitionend', content._faqHideHandler)
      content._faqHideHandler = null
    }

    if (content._faqHideTimer) {
      clearTimeout(content._faqHideTimer)
      content._faqHideTimer = null
    }
  }
}
